import type { ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell, useIsWide } from '@/ui'
import type { DetailState } from '@/lib/detail'
import Login from './routes/Login'
import Analytics from './routes/analytics'
import WebUsageDetail from './routes/analytics/web/WebUsageDetail'
import WebUsageList from './routes/analytics/web/WebUsageList'
import PlaySlots from './routes/playslots/PlaySlots'
import Jobs from './routes/system/Jobs'
import Settings from './routes/system/Settings'
import { redirectRoutes } from './routes/_redirects'
import Reports from './routes/reports/Reports'
import Insight from './routes/insight/Insight'
import Today from './routes/today/Today'
import Movement from './routes/movement/Movement'
import Research from './routes/research/Research'
import Compare from './routes/compare/Compare'
import AppList from './routes/apps/AppList'
import { AppWorkspaceRoute } from './routes/apps/AppWorkspace'
import { KeywordRoute } from './routes/apps/KeywordDrawer'

/** Pages of the new layout that are built. */
const NEW_ROUTES: { path: string; element: ReactNode }[] = [
  { path: '/', element: <Today /> },
  { path: '/reports', element: <Reports /> },
  { path: '/reports/:date', element: <Reports /> },
  { path: '/insight', element: <Insight /> },
  { path: '/movement', element: <Movement /> },
  { path: '/research', element: <Research /> },
  { path: '/compare', element: <Compare /> },
  { path: '/apps', element: <AppList /> },
  // An app's workspace: the tab is part of the address (keywords · competitors
  // · score · usage). The bare `/apps/:id` is a redirect to the keywords.
  { path: '/apps/:id/:tab', element: <AppWorkspaceRoute /> },
  // A keyword's detail. Opened from a list it is drawn over that list (see
  // DETAIL_ROUTES); opened by its address it brings the app's page itself.
  { path: '/apps/:id/keywords/:kid', element: <KeywordRoute /> },
  { path: '/analytics', element: <Analytics /> },
  { path: '/analytics/:appId', element: <Analytics /> },
  // 사용량's other tab: web services, read from Cloudflare Analytics Engine.
  { path: '/analytics/web', element: <WebUsageList /> },
  { path: '/analytics/web/:serviceId', element: <WebUsageDetail /> },
  { path: '/playslots', element: <PlaySlots /> },
  { path: '/jobs', element: <Jobs /> },
  { path: '/settings', element: <Settings /> },
]

/** The details drawn over the page they were opened from, from 1024px up. */
const DETAIL_ROUTES: { path: string; element: ReactNode }[] = [
  { path: '/apps/:id/keywords/:kid', element: <KeywordRoute overlay /> },
]

export const MOUNTED_PATHS = NEW_ROUTES.map((r) => r.path)

export default function App() {
  const location = useLocation()
  const wide = useIsWide()
  // A detail opened from a page carries that page as its background: on a
  // wide screen the page stays drawn (and keeps its filters and scroll) with
  // the detail as a drawer on top. Below 1024px the detail is a page.
  const background = wide ? (location.state as DetailState | null)?.background : undefined
  return (
    <>
      <Routes location={background ?? location}>
        <Route path="/login" element={<Login />} />
        <Route element={<AppShell />}>
          {NEW_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          {redirectRoutes()}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      {background && (
        <Routes>
          {DETAIL_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          <Route path="*" element={null} />
        </Routes>
      )}
    </>
  )
}
